import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { StatCard } from "../../components/admin/StatCard";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useAdminMetrics } from "../../hooks/useAdminMetrics";
import { titleCase } from "../../lib/format";

function entries(source?: Record<string, number>) {
  return Object.entries(source ?? {});
}

export function AdminMetricsPage() {
  const { data: metrics, loading, error } = useAdminMetrics();

  if (loading) return <FullPageLoader label="Loading metrics" />;
  if (error) return <EmptyState title="Unable to load metrics.">{error}</EmptyState>;

  return (
    <div>
      <AdminPageHeader title="Metrics" description="Operational platform metrics for access, tenancy and control health." />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Users" value={metrics?.user_count ?? 0} />
        <StatCard label="Disabled users" value={metrics?.disabled_users ?? 0} />
        <StatCard label="Old pending invites" value={metrics?.pending_invites_older_than_7_days ?? 0} />
        <StatCard label="Avg members per tenant" value={metrics?.average_members_per_tenant ?? 0} />
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <AdminPanel title="Tenants by status">
          <MetricBars values={entries(metrics?.tenant_counts)} />
        </AdminPanel>
        <AdminPanel title="Users by role">
          <MetricBars values={entries(metrics?.role_counts)} />
        </AdminPanel>
        <AdminPanel title="Invites by status">
          <MetricBars values={entries(metrics?.invite_counts)} />
        </AdminPanel>
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <AdminPanel title="Data integrity signals">
          <div className="space-y-3 text-sm text-graphite/70">
            <p>Tenants with no owner: <strong>{metrics?.tenants_without_owner ?? 0}</strong></p>
            <p>Tenants with no active members: <strong>{metrics?.tenants_without_active_members ?? 0}</strong></p>
            <p>Suspended tenants: <strong>{metrics?.tenant_counts?.suspended ?? 0}</strong></p>
          </div>
        </AdminPanel>
        <AdminPanel title="Feature flag usage">
          <MetricBars values={entries(metrics?.feature_flag_usage)} />
        </AdminPanel>
      </div>
    </div>
  );
}

function MetricBars({ values }: { values: Array<[string, number]> }) {
  const max = Math.max(...values.map(([, value]) => value), 1);

  if (!values.length) {
    return <p className="text-sm text-graphite/65">No data yet.</p>;
  }

  return (
    <div className="space-y-3">
      {values.map(([label, value]) => (
        <div key={label}>
          <div className="mb-1 flex justify-between text-sm">
            <span className="font-medium text-ink">{titleCase(label)}</span>
            <span className="text-graphite/65">{value}</span>
          </div>
          <div className="h-2 rounded-full bg-line">
            <div className="h-2 rounded-full bg-pine" style={{ width: `${(value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
